import type {
  DesktopCatalogue,
  DesktopPrinter,
  DesktopSales,
  DesktopSequences,
  DesktopShift,
  DesktopStock,
  DesktopSync,
  DesktopTerminal,
  DesktopTickets,
  DesktopUpdates,
} from '@repo/desktop-bridge';
import { DESKTOP_BRIDGE_KEY } from '@repo/desktop-bridge';

/**
 * The Electron shell this app may or may not be running inside.
 *
 * The same React build serves the back office in a browser and the tills as a
 * desktop app, and only the second has a bridge — the outbox, the held
 * catalogue, the roster and the printer are all on the device. **Answering
 * `null` is the ordinary case, not a failure**, and every caller here has to be
 * able to render something sensible without one.
 *
 * **The types come from `@repo/desktop-bridge`**, which is the same definition
 * the preload script implements. Until FE026 this file held a hand-copied slice
 * of two of them, with a header saying to promote the package and delete the
 * copy the moment the renderer started *making* writes rather than counting
 * refused ones. That moment is a till selling a ticket: the shapes now carry an
 * amount in paise and a client-minted id, and two copies of those drifting apart
 * is a money bug that typechecks on both sides.
 *
 * Checked shape by shape rather than trusted. The bridge is injected by a
 * preload script, and this bundle is served over `app://` from a build that can
 * be older or newer than the shell around it — so "the property is there" and
 * "it answers the calls I am about to make" are different questions, and a
 * version skew should render a plain message rather than a `TypeError`.
 */

/** Whether `value` is an object carrying a function under each name. */
function answersAll(value: unknown, names: readonly string[]): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return names.every((name) => typeof candidate[name] === 'function');
}

function bridgeObject(): Record<string, unknown> | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const bridge: unknown = (window as { [DESKTOP_BRIDGE_KEY]?: unknown })[DESKTOP_BRIDGE_KEY];

  return typeof bridge === 'object' && bridge !== null ? (bridge as Record<string, unknown>) : null;
}

function slice<T>(name: string, calls: readonly string[]): T | null {
  const bridge = bridgeObject();

  if (bridge === null) {
    return null;
  }

  const part: unknown = bridge[name];

  return answersAll(part, calls) ? (part as T) : null;
}

/**
 * Each list below is **the calls this app actually makes**, not the whole
 * interface.
 *
 * That is the difference between detecting a capability and demanding a version.
 * A shell one release ahead of this bundle may well carry a call nothing here
 * has heard of, and a shell one release behind may be missing one nothing here
 * uses; refusing either would turn a screen off over a method it was never going
 * to invoke. Add to a list when a caller starts making the call, and not before.
 */

/** The terminal's write queue, or `null` off a terminal. */
export const desktopSync = (): DesktopSync | null =>
  slice<DesktopSync>('sync', ['status', 'blocked']);

/**
 * **The same slice again, asked for with one more call** — reading the queue
 * and being able to send one of its refusals again are two capabilities, and
 * the split is the point.
 *
 * A shell one release behind this bundle can list quarantined writes perfectly
 * well and simply cannot offer one back. Folding `requeue` into the list above
 * would answer `null` for the whole slice and replace the screen with "this
 * queue lives on the terminal" — on a terminal — which is the version-skew
 * failure this file exists to avoid, and it would do it on the one screen whose
 * job is to show somebody a sale that has gone missing.
 *
 * So an older shell lists the pile and does not draw the control. Hiding a
 * control that cannot work is `docs/ui-conventions.md`'s rule for a role, and
 * it is the right answer for a missing channel too.
 */
export const desktopSyncRequeue = (): DesktopSync | null =>
  slice<DesktopSync>('sync', ['status', 'blocked', 'requeue']);

/**
 * The receipt printer — **`status` only, and deliberately not `print`.**
 *
 * Nothing in the renderer prints. A receipt is printed by the main process as
 * part of the one call that rings up a basket, because allocating a number,
 * queueing the write and putting paper in somebody's hand are one act
 * (`desktop-bridge/bridge.ts`). What a screen may do is *ask whether printing
 * will work at all*, which is a different question and one worth asking before
 * the money rather than after it.
 */
export const desktopPrinter = (): DesktopPrinter | null =>
  slice<DesktopPrinter>('printer', ['status']);

/**
 * **The printer again, asked for with the three calls that set it** (#261) —
 * the setting kept on the till, and a test page through the transport a
 * ticket goes through. A shell one release behind has none of them, and on it
 * the sign-on screen simply offers no printer control.
 */
export const desktopPrinterSetup = (): DesktopPrinter | null =>
  slice<DesktopPrinter>('printer', ['setup', 'save', 'testPage']);

/**
 * **And once more with `printers`** (#282) — the printers Windows has on this
 * PC, for the setup screen to pick from. Apart from the slice above for the
 * reason `desktopSyncRequeue` gives: a shell one release behind still sets its
 * printer by typing, and only the list is missing.
 */
export const desktopPrinterList = (): DesktopPrinter | null =>
  slice<DesktopPrinter>('printer', ['setup', 'save', 'testPage', 'printers']);

/**
 * **The build the running shell reports (#306)**, from `app.getVersion()` in the
 * main process — the one source, and deliberately not a constant baked into this
 * bundle: a bundle and the shell around it can disagree, and two installs on one
 * laptop meant Settings said 1.0.1 while the window open was 1.0.0.
 *
 * Top-level on the bridge rather than a slice, so it is checked on its own.
 * `null` off a terminal and on a shell without the call: draw nothing.
 */
export function desktopAppVersion(): (() => Promise<string>) | null {
  const bridge = bridgeObject();
  const call = bridge?.getAppVersion;

  return typeof call === 'function' ? () => (call as () => Promise<string>).call(bridge) : null;
}

/** A new version waiting to install (#288). `null` off a terminal or on an older shell: no line, nothing else. */
export const desktopUpdates = (): DesktopUpdates | null =>
  slice<DesktopUpdates>('updates', ['waiting']);

/** What this outlet sells, held so the till keeps selling through an outage. */
export const desktopCatalogue = (): DesktopCatalogue | null =>
  slice<DesktopCatalogue>('catalogue', ['held', 'status']);

/**
 * What the records say is on the shelves here — **a warning, and never a gate.**
 *
 * `null` off a terminal, and `null` on a shell one release behind this bundle,
 * and both answers cost the same thing: the counter loses a warning and keeps
 * selling. That is not a fallback this file is settling for, it is the decided
 * behaviour — CL-801, the till warns and never refuses — so a screen that
 * receives `null` here must render exactly as it did before stock existed.
 *
 * `refresh` is in the list rather than beside it, because a level a person
 * cannot ask a fresh copy of is decoration: the figure is stale by construction
 * and the only useful response to a warning is to see how old it is and get a
 * newer one. A shell that can answer the position but not re-read it would draw
 * a number with no way to act on it, so the two are one capability.
 */
export const desktopStock = (): DesktopStock | null =>
  slice<DesktopStock>('stock', ['held', 'status', 'refresh']);

/**
 * Why this terminal's numbering starts where it does.
 *
 * `null` off a terminal, `null` on an older shell, and `null` on the great
 * majority of tills that have always had their own counters — three different
 * reasons for one answer, and every one of them means the same thing on screen:
 * draw nothing. There is no failure mode here to report, because a device that
 * has never needed recovering is the ordinary case rather than a gap.
 *
 * Read-only, and deliberately so. Nothing in this list can start a recovery: the
 * decision turns on whether a local counter row exists, which only the main
 * process can see, and a control that could seed a counter is a control that
 * could reprint a number already in a visitor's hand.
 */
export const desktopSequences = (): DesktopSequences | null =>
  slice<DesktopSequences>('sequences', ['recovery']);

/** Who is standing at the till, and starting a shift for them. */
export const desktopShift = (): DesktopShift | null =>
  slice<DesktopShift>('shift', ['start', 'current', 'end']);

/**
 * The device's own identity, and the one call that gives it one.
 *
 * Status only in the other direction — the lease never crosses back. `register`
 * is the exception that proves it: the renderer is the courier, because it is
 * the process that made the API call, and it hands the answer straight over
 * without keeping it.
 */
export const desktopTerminal = (): DesktopTerminal | null =>
  slice<DesktopTerminal>('terminal', ['identity', 'register']);

/**
 * **The outlets this machine sells for, and choosing one** — the same slice
 * again, asked for with the two calls issue #243 added.
 *
 * Apart from {@link desktopTerminal} for the reason the sales slices are split:
 * a shell one release behind this bundle holds one registration and has
 * neither call, and it must still register and still open its till. On such a
 * shell the till names no outlet and offers no switch — which is exactly what a
 * till holding one registration needs.
 */
export const desktopRegistrations = (): DesktopTerminal | null =>
  slice<DesktopTerminal>('terminal', ['identity', 'registrations', 'activate']);

/**
 * Selling admission.
 *
 * The one call that takes money, and deliberately the only one on it: **there
 * is no ticket reprint and there must never be one**, because the printed
 * artefact admits somebody and a second copy is a free admission (CL-001).
 *
 * The API publishes no ticket reprint at all. A receipt reprint is open to a
 * terminal principal (`blossom-rest-api#411`), and the till took it away
 * anyway in #294 — so neither is reachable from here, for two different
 * reasons.
 */
export const desktopTickets = (): DesktopTickets | null =>
  slice<DesktopTickets>('tickets', ['issueBasket']);

/**
 * Selling goods over a counter.
 *
 * One call, like admission's, and for the same reason: pricing the basket,
 * spending a number, queueing the write and printing the receipt are one act,
 * and nothing between them is the renderer's to sequence.
 */
export const desktopSales = (): DesktopSales | null => slice<DesktopSales>('sales', ['record']);

/**
 * **Selling into the other outlets this till holds (#284)** — joining one to
 * the sale at the counter, and recording a sale there. A capability of its own
 * off the same slice, asked for separately: a shell one release behind this
 * bundle sells exactly as before, one outlet at a time, rather than losing the
 * counter over a call it lacks. And a capability rather than an argument on
 * `record`, which an older shell would ignore and book to the wrong outlet.
 */
export const desktopSalesAcross = (): DesktopSales | null =>
  slice<DesktopSales>('sales', ['record', 'joinOutlet', 'recordAt']);

/**
 * **What the running shift has taken** — the same slice again, and a third
 * capability for the reason there is a second one.
 *
 * A shell one release behind this bundle rings up baskets perfectly well and
 * simply cannot count a drawer. Folding `shift` into either list above would
 * answer `null` for the whole slice and take the shop counter away over a
 * report, which is the version-skew failure this file exists to avoid. So on an
 * older shell the till sells, ends a shift the way it always did, and the count
 * is not drawn.
 *
 * Kept apart from {@link desktopRecentSales} because they serve two screens with
 * two different fallbacks: losing the count means ending a shift without one,
 * and losing the list means a cashier cannot look up what they sold. Neither
 * should take the other with it.
 */
export const desktopShiftTakings = (): DesktopSales | null =>
  slice<DesktopSales>('sales', ['record', 'shift']);

/**
 * **The day's sales at this till** — a list to read (#290), and since #294
 * nothing more: a refund and a reprint are a manager's, in the back office.
 */
export const desktopRecentSales = (): DesktopSales | null =>
  slice<DesktopSales>('sales', ['record', 'recent']);
